import { motion } from "motion/react";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import WhatsAppFloating from "../../../components/feature/WhatsAppFloating";
import CookieBanner from "../../../components/feature/CookieBanner";
import QuoteFormSection from "../../home/components/QuoteFormSection";
import SEO from "@/components/feature/SEO";

const ventajas = [
  {
    emoji: "🛡️",
    title: "Lona protegida",
    desc: "La caja de aluminio protege la lona del sol, la lluvia y el salitre. Hasta un 50% más de vida útil que un toldo convencional.",
  },
  {
    emoji: "🎨",
    title: "Diseño moderno integrado",
    desc: "Cuando está cerrado se ve como una caja limpia y elegante en tu fachada. No queda nada colgando ni enrollado a la vista.",
  },
  {
    emoji: "🌊",
    title: "Apto para zonas costeras",
    desc: "Perfecto para Málaga, Marbella y toda la Costa del Sol. El cofre protege la lona del salitre y la humedad marina.",
  },
  {
    emoji: "⚡",
    title: "Compatible con motorización",
    desc: "Motorizamos tu toldo cofre con mando a distancia, sensor de viento o integración con domótica. Cómodo y moderno.",
  },
];

const tipos = [
  {
    title: "Semi-cofre",
    desc: "La lona se guarda protegida en la caja, pero los brazos quedan visibles. Económico y práctico.",
    best: "Balcón pequeño, fachada ajustada",
  },
  {
    title: "Cofre completo",
    desc: "Todo protegido: lona y brazos dentro de la caja cuando el toldo está cerrado. Máxima protección.",
    best: "Terraza, porche, zonas costeras",
  },
  {
    title: "Cofre extra premium",
    desc: "Caja independiente para los brazos, el más robusto y estético. Ideal para superficies grandes y hostelería.",
    best: "Terrazas XL, restaurantes, comercios",
  },
];

const precios = [
  { tipo: "Semi-cofre balcón", precio: "700 - 1.000 €" },
  { tipo: "Cofre completo terraza", precio: "1.200 - 2.000 €" },
  { tipo: "Cofre extra premium", precio: "1.800 - 2.500 €" },
];

const faqs = [
  {
    q: "¿Cuánto dura un toldo cofre en Málaga?",
    a: "Con la protección del cofre, un toldo de calidad dura entre 12 y 18 años en la Costa del Sol. Sin cofre la vida útil suele ser de 8-10 años por el desgaste del sol y el salitre. El cofre alarga la vida útil hasta un 50%.",
  },
  {
    q: "¿Vale la pena el extra precio vs toldo normal?",
    a: "Sí, especialmente si vives cerca del mar en Málaga, Marbella o Fuengirola. El cofre protege la lona del salitre que acelera el deterioro. A largo plazo compensas la inversión evitando sustituciones prematuras de lona.",
  },
  {
    q: "¿Se puede motorizar un toldo cofre?",
    a: "Sí, todos nuestros toldos cofre se pueden motorizar con motor tubular Somfy. También puedes añadir sensor de viento que recoge el toldo automáticamente si sopla fuerte.",
  },
  {
    q: "¿Cómo se limpia un toldo cofre?",
    a: "Solo necesitas limpiar la caja exterior con agua y jabón neutro de vez en cuando. La lona está protegida dentro, así que no se ensucia ni se decolora como un toldo convencional.",
  },
  {
    q: "¿Necesita mantenimiento?",
    a: "Muy poco. Revisamos los brazos, engrasamos las articulaciones y revisamos el motor anualmente si está motorizado. El mantenimiento anual cuesta 60 € y lo incluimos en la garantía de 2 años.",
  },
];

export default function ToldosCofre() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Toldos Cofre Málaga | Lona Protegida con Diseño Moderno"
        description="Toldos cofre en Málaga: lona protegida cuando cerrado, mayor durabilidad, diseño moderno. Ideal Costa del Sol. ☎ 951 12 07 70"
        canonical="/toldos/toldos-cofre/"
        ogType="website"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Toldos", path: "/toldos" }, { name: "Toldos cofre" }]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-[60%] flex flex-col gap-6">
                <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill w-fit">
                  <span>⚡</span> Lona siempre protegida
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-text-primary">
                  Toldos Cofre en Málaga: Protección y Diseño
                </h1>
                <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl">
                  La lona protegida del sol y la lluvia cuando está cerrado. Mayor durabilidad y diseño moderno.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg md:text-xl px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                  >
                    <span>📞</span> 951 12 07 70
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Pedir presupuesto
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-2">
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> +10 años · desde 2015</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Seguro RC</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Garantía 2 años</span>
                </div>
              </div>
              <div className="w-full lg:w-[40%] relative">
                <div className="relative rounded-3xl overflow-hidden lg:rotate-1 shadow-xl">
                  <div className="absolute inset-0 bg-blue-900/35 z-10 pointer-events-none"></div>
                  <img
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop"
                    alt="Toldos cofre en Málaga - lona protegida con diseño moderno"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ¿QUÉ ES? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Qué es un toldo cofre?</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full mb-6"></div>
              <p className="font-body text-lg text-text-secondary leading-relaxed">
                Es un sistema de toldo con <strong className="text-text-primary">caja de aluminio</strong> que protege completamente la lona y los mecanismos cuando el toldo está cerrado. Ideal para zonas costeras como Málaga porque el <strong className="text-text-primary">salitre marino</strong> acelera el deterioro de las lonas expuestas. Con el cofre, tu toldo dura años más y siempre luce como nuevo.
              </p>
            </div>
          </div>
        </section>

        {/* VENTAJAS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Ventajas de los toldos cofre</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ventajas.map((v, i) => (
                <motion.div
                  key={v.title}
                  className="bg-surface-alt rounded-[20px] p-8 border-t-[6px] border-primary shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl mb-4">{v.emoji}</div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{v.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* TIPOS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Tipos de cofre</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {tipos.map((t, i) => (
                <motion.div
                  key={t.title}
                  className="bg-white rounded-[20px] p-8 shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className="ri-archive-2-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{t.title}</h3>
                  <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">{t.desc}</p>
                  <div className="inline-block bg-accent/80 text-text-primary font-body font-semibold text-xs px-3 py-1.5 rounded-pill">
                    Ideal para: {t.best}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">Precios orientativos</h2>
            <p className="font-body text-lg text-text-secondary text-center mb-10 max-w-2xl mx-auto">
              Cada toldo se fabrica a medida. Estos precios incluyen estructura, lona de calidad, instalación y garantía de 2 años.
            </p>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl overflow-hidden">
              {precios.map((p, i) => (
                <div
                  key={p.tipo}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="font-body text-base md:text-lg text-text-primary">{p.tipo}</span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según dimensiones y acabados. Presupuesto exacto sin compromiso tras visita técnica.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Preguntas frecuentes sobre toldos cofre</h2>
              <div className="flex flex-col gap-4">
                {faqs.map((f, i) => (
                  <motion.div
                    key={i}
                    className="bg-white rounded-2xl p-6 shadow-sm"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2" itemProp="name">{f.q}</h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="font-body text-sm text-text-secondary leading-relaxed" itemProp="text">{f.a}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}